import React, { useState, useMemo, useRef } from 'react';
import { FiPlus, FiMessageSquare, FiChevronLeft, FiChevronRight, FiTrash2, FiX, FiDownload, FiSearch, FiUpload, FiShield } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import Logo from '../UI/Logo';
import ThemeToggle from '../UI/ThemeToggle';
import NotificationSettings from '../UI/NotificationSettings';
import ApiModeSwitcher from '../ApiModeSwitcher';
import ConfirmationModal from '../UI/ConfirmationModal';
import UserProfile from '../Auth/UserProfile';
import ChatAdminPanel from './ChatAdminPanel';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { useMsal, useAccount } from '@azure/msal-react';

interface SidebarProps {
  onClose?: () => void;
}

// Admin email list - must match backend list
const CHAT_ADMIN_EMAILS = [
  "paymanshu.sharma@maersk.com",
  "shantanu.rastogi@maersk.com",
  "srikant.yadav1@maersk.com",
  "krisha.joshi@maersk.com",
  "nikhil.nagesh@maersk.com"
]

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { conversations, currentConversationId, loadConversation, deleteConversation, startNewConversation, exportConversationsAsJSON, importConversationsFromJSON, isSidebarCollapsed, setSidebarCollapsed, isConversationInProgress } = useChat();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  // Check if current user is admin
  const isAdmin = useMemo(() => {
    const userEmail = account?.username || '';
    return CHAT_ADMIN_EMAILS.includes(userEmail.toLowerCase());
  }, [account]);

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    
    const query = searchQuery.toLowerCase();
    return conversations.filter(conversation => 
      conversation.title.toLowerCase().includes(query) ||
      conversation.lastMessagePreview?.toLowerCase().includes(query) ||
      conversation.messages.some(message => 
        typeof message.content === 'string' && 
        message.content.toLowerCase().includes(query)
      )
    );
  }, [conversations, searchQuery]);

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setSidebarCollapsed(!isSidebarCollapsed);
  };

  // Start a new conversation with storage check
  const handleNewChat = () => {
    const newConversationId = startNewConversation();
    if (!newConversationId) {
      // Storage warning will be shown by the context
      console.warn('Failed to create new conversation due to storage constraints');
    }
  };

  // Format date for conversation list
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Handle delete conversation
  const handleDeleteConversation = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation(); // Prevent triggering the conversation load
    setConversationToDelete(conversationId);
    setDeleteModalOpen(true);
  };

  // Confirm delete conversation
  const confirmDeleteConversation = () => {
    if (conversationToDelete) {
      deleteConversation(conversationToDelete);
      setConversationToDelete(null);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setConversationToDelete(null);
  };

  // Handle export conversations
  const handleExportConversations = () => {
    exportConversationsAsJSON();
  };

  // Handle import conversations
  const handleImportConversations = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection for import
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const result = await importConversationsFromJSON(file);
      if (!result.success) {
        console.error('Import failed:', result.message);
      }
    }
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Toggle search visibility
  const toggleSearch = () => {
    setShowSearch(!showSearch);
    if (showSearch) {
      setSearchQuery(''); // Clear search when hiding
    }
  };

  return (
    <div className={`relative h-full ${isSidebarCollapsed ? 'w-20' : 'w-72'} transition-all duration-500 ease-in-out bg-secondary dark:bg-dark-secondary border-r border-neutral-dark dark:border-dark-border flex flex-col`}>
      {/* Mobile close button (only show if onClose is provided) */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute -right-4 top-5 z-50 p-2 bg-white dark:bg-dark-accent shadow-md rounded-full text-text-primary dark:text-dark-text border border-neutral-dark dark:border-dark-border hover:bg-neutral hover:dark:bg-dark-border transition-colors md:hidden"
          aria-label="Close sidebar"
        >
          <FiX size={16} />
        </button>
      )}
      {/* Collapse toggle button */}
      <button 
        onClick={toggleSidebar}
        className="absolute -right-4 top-5 z-50 p-2 bg-white dark:bg-dark-accent shadow-md rounded-full text-text-primary dark:text-dark-text border border-neutral-dark dark:border-dark-border hover:bg-neutral hover:dark:bg-dark-border transition-colors md:block hidden"
        aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isSidebarCollapsed ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
      </button>

      {/* Logo and header */}
      <div className="p-4 border-b border-neutral-dark dark:border-dark-border">
        <Logo isCollapsed={isSidebarCollapsed} />
      </div>

      {/* New chat button */}
      <div className={`p-4 ${isSidebarCollapsed ? 'flex justify-center' : ''}`}>
        <button
          onClick={handleNewChat}
          className={`flex items-center ${isSidebarCollapsed ? 'justify-center px-2 py-3' : 'float-left px-4 py-2'} w-full bg-primary text-white rounded-xl transition-colors glow-cta halo-container accent-halo`}
        >
          <FiPlus size={isSidebarCollapsed ? 20 : 16} />
          {!isSidebarCollapsed && <span className="ml-2 font-medium">New Chat</span>}
        </button>
      </div>

      {/* Conversation list */}
      {!isSidebarCollapsed && (
        <div className="flex-1 overflow-y-auto">
          {/* Header with title and action buttons */}
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-medium text-text-tertiary dark:text-dark-textSecondary uppercase font-display glow-sm">
                Recent conversations
              </div>
              <div className="flex items-center gap-2">
                {/* Import button */}
                <button
                  onClick={handleImportConversations}
                  className="p-1.5 rounded-lg text-text-tertiary dark:text-dark-textSecondary transition-colors hover-glow-sm focus:text-primary focus:glow-md"
                  aria-label="Import conversations from JSON"
                  title="Import conversations from JSON"
                >
                  <FiUpload size={14} />
                </button>
                
                {/* Export button */}
                <button
                  onClick={handleExportConversations}
                  className="p-1.5 rounded-lg text-text-tertiary dark:text-dark-textSecondary transition-colors hover-glow-sm focus:text-primary focus:glow-md"
                  aria-label="Export conversations as JSON"
                  title="Export conversations as JSON"
                >
                  <FiDownload size={14} />
                </button>
                
                {/* Search toggle button */}
                <button
                  onClick={toggleSearch}
                  className={`p-1.5 rounded-lg transition-colors hover-glow-sm ${
                    showSearch 
                      ? 'text-primary bg-neutral dark:bg-dark-accent glow-sm' 
                      : 'text-text-tertiary dark:text-dark-textSecondary'
                  }`}
                  aria-label="Toggle search conversations"
                  title="Search conversations"
                >
                  <FiSearch size={14} />
                </button>
              </div>
            </div>
            
            {/* Search input - animated slide down/up */}
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showSearch ? 'max-h-16 opacity-100 mb-3' : 'max-h-0 opacity-0 mb-0'
            }`}>
              <div className="relative">
                <FiSearch size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-dark-textSecondary" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-neutral dark:bg-dark-accent border border-neutral-dark dark:border-dark-border rounded-lg focus:outline-none focus:border-primary dark:focus:border-primary text-text-primary dark:text-dark-text placeholder-text-tertiary dark:placeholder-dark-textSecondary transition-all duration-200"
                />
              </div>
            </div>
          </div>
          
          {/* Hidden file input for import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <div className="px-3 space-y-1">
            {filteredConversations.length === 0 ? (
              <div className="text-center py-4 text-text-tertiary dark:text-dark-textSecondary text-sm">
                {searchQuery.trim() ? 'No conversations match your search' : 'No conversations yet'}
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`relative group flex items-start p-2 rounded-xl hover-glow-lg transition-all duration-200 ${
                    currentConversationId === conversation.id ? 'bg-primary/10 dark:bg-primary/5 border border-primary/50 glow-md shadow-glow-md' : ''
                  }`}
                >
                  {/* Main conversation button */}
                  <button
                    onClick={() => loadConversation(conversation.id)}
                    className="flex-1 flex items-start text-left min-w-0"
                  >
                    {isConversationInProgress(conversation.id) && currentConversationId !== conversation.id ? (
                      <svg className="animate-spin mt-0.5 mr-3 h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
                      </svg>
                    ) : (
                      <FiMessageSquare className="mt-0.5 mr-3 text-text-tertiary dark:text-dark-textSecondary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary dark:text-dark-text truncate">
                        {conversation.title}
                      </div>
                      {conversation.lastMessagePreview && (
                        <div className="text-xs text-text-tertiary dark:text-dark-textSecondary truncate">
                          {conversation.lastMessagePreview}
                        </div>
                      )}
                      <div className="text-xs text-text-tertiary dark:text-dark-textSecondary">
                        {formatDate(conversation.timestamp)}
                      </div>
                    </div>
                  </button>
                  
                  {/* Delete button - appears on hover */}
                  <button
                    onClick={(e) => handleDeleteConversation(e, conversation.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex-shrink-0 ml-2"
                    aria-label="Delete conversation"
                    title="Delete conversation"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {isSidebarCollapsed && <div className="flex-1"></div>} {/* Spacer when collapsed */}


      {/* Theme, notifications, and API mode toggles */}
      <div className={`p-4 border-t border-neutral-dark dark:border-dark-border ${isSidebarCollapsed ? 'flex flex-col justify-center space-y-2' : 'flex flex-col gap-4'}`}>
        {!isSidebarCollapsed && process.env.NODE_ENV !== 'production' && <ApiModeSwitcher />}
        
        {/* Theme and notification buttons: when expanded show side-by-side split evenly; when collapsed stack vertically */}
        <div className={`w-full ${isSidebarCollapsed ? 'flex flex-col items-center gap-2' : 'flex flex-row items-stretch gap-2'}`}>
          <div className={isSidebarCollapsed ? 'w-full flex items-center justify-center' : 'flex-1 flex items-center justify-center px-1'}>
            <NotificationSettings />
          </div>
          <div className={isSidebarCollapsed ? 'w-full flex items-center justify-center' : 'flex-1 flex items-center justify-center px-1'}>
            <ThemeToggle />
          </div>
          {isAdmin && (
            <div className={isSidebarCollapsed ? 'w-full flex items-center justify-center' : 'flex-1 flex items-center justify-center px-1'}>
              <button
                onClick={() => setShowAdminPanel(true)}
                className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                title="Admin Panel"
                aria-label="Open Admin Panel"
              >
                <FiShield size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* User info/settings */}
      {FEATURE_FLAGS.REQUIRE_AUTHENTICATION ? (
        <div className={`p-4 border-t border-neutral-dark dark:border-dark-border ${isSidebarCollapsed ? 'flex justify-center' : ''}`}>
          <UserProfile isCollapsed={isSidebarCollapsed} />
        </div>
      ) : (
        <div className={`p-4 border-t border-neutral-dark dark:border-dark-border ${isSidebarCollapsed ? 'flex justify-center' : ''}`}>
          <div className={`flex items-center gap-3 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            {!isSidebarCollapsed && (
              <div className="flex flex-col">
                <span className="text-neutral-medium dark:text-dark-text-secondary text-sm font-medium">
                  Development Mode
                </span>
                <span className="text-neutral-light dark:text-dark-text-tertiary text-xs">
                  Authentication disabled
                </span>
              </div>
            )}
            {isSidebarCollapsed && (
              <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">DEV</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteConversation}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Chat Admin Panel */}
      {isAdmin && showAdminPanel && (
        <ChatAdminPanel
          adminEmail={account?.username || ''}
          onClose={() => setShowAdminPanel(false)}
        />
      )}
    </div>
  );
};

export default Sidebar;
